import { useMemo } from 'react';

interface VoltageComboSelectorProps {
  availableCombos: string[];
  selectedCombo: string | null;
  onComboChange: (combo: string) => void;
}

/**
 * Dropdown selectors for reference voltage and reference electrode.
 * Combo keys are "ref_voltage|ref_electrode" strings.
 * Hides a dropdown if only one option exists for that dimension.
 */
const VoltageComboSelector = ({
  availableCombos,
  selectedCombo,
  onComboChange,
}: VoltageComboSelectorProps) => {
  const { voltages, electrodes } = useMemo(() => {
    const vSet = new Set<string>();
    const eSet = new Set<string>();
    for (const combo of availableCombos) {
      const parts = combo.split('|');
      if (parts[0]) vSet.add(parts[0]);
      if (parts[1]) eSet.add(parts[1]);
    }
    return { voltages: [...vSet].sort(), electrodes: [...eSet].sort() };
  }, [availableCombos]);

  const selectedParts = selectedCombo?.split('|') || [];
  const selectedVoltage = selectedParts[0] || voltages[0] || '';
  const selectedElectrode = selectedParts[1] || electrodes[0] || '';

  const handleVoltageChange = (v: string) => {
    const newCombo = `${v}|${selectedElectrode}`;
    if (availableCombos.includes(newCombo)) {
      onComboChange(newCombo);
    } else {
      // Find first combo with this voltage
      const match = availableCombos.find((c) => c.startsWith(`${v}|`));
      if (match) onComboChange(match);
    }
  };

  const handleElectrodeChange = (e: string) => {
    const newCombo = `${selectedVoltage}|${e}`;
    if (availableCombos.includes(newCombo)) {
      onComboChange(newCombo);
    } else {
      const match = availableCombos.find((c) => c.endsWith(`|${e}`));
      if (match) onComboChange(match);
    }
  };

  if (availableCombos.length === 0) return null;

  return (
    <>
      {voltages.length > 1 && (
        <label>
          Ref. Voltage{' '}
          <select value={selectedVoltage} onChange={(e) => handleVoltageChange(e.target.value)}>
            {voltages.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </label>
      )}
      {electrodes.length > 1 && (
        <label>
          Ref. Electrode{' '}
          <select value={selectedElectrode} onChange={(e) => handleElectrodeChange(e.target.value)}>
            {electrodes.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
        </label>
      )}
    </>
  );
};

export default VoltageComboSelector;
